import { Suspense, useState, useEffect } from 'react'
import { Outlet, useNavigate, Link } from 'react-router-dom'
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'react-bootstrap'
import Menu from '@/views/menu'
import Footer from '@/components/Footer'
import logoL from '@/assets/images/logo_L.png'
import logoM from '@/assets/images/logo_m.png'
import { auth } from '@/config/firebase'
import { signOut } from 'firebase/auth'
import { useSchoolAccount } from '@/context/SchoolAccountContext'
import { DotLoader } from 'react-spinners'

function MainLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const { schoolAccount, isLoading: schoolAccountLoading, refresh } = useSchoolAccount()
  const navigate = useNavigate()

  // 認証とSchoolAccountのチェック
  useEffect(() => {
    // SchoolAccountの読み込みが完了したらチェック
    if (schoolAccountLoading) {
      return
    }

    const user = auth.currentUser
    // 未ログインの場合はログイン画面へ
    if (!user) {
      navigate('/login', { replace: true })
      return
    }
    // ログイン済みだが学校アカウントが存在しない／無効の場合は 403 エラーページへ
    if (!schoolAccount) {
      navigate('/error/403', { replace: true })
      return
    }
    if (!schoolAccount.lastLoginAt) {
      navigate('/first-login', { replace: true })
      return
    }
  }, [schoolAccount, schoolAccountLoading, navigate])

  // モバイル判定
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      // モバイルの場合はデフォルトで閉じる
      if (window.innerWidth < 768) {
        setIsMenuOpen(false)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  // モバイルでメニューが開いている時、背景をクリックで閉じる
  const handleOverlayClick = () => {
    if (isMobile && isMenuOpen) {
      setIsMenuOpen(false)
    }
  }

  // ログアウト処理
  const handleLogout = async () => {
    try {
      // SchoolAccountContextを初期化
      await refresh()
      // Firebase認証からログアウト
      await signOut(auth)
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('ログアウトエラー:', error)
    }
  }

  // ロード中
  if (schoolAccountLoading) {
    return (
      <div className="loading-overlay">
        <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
      </div>
    )
  }

  // リダイレクト待ち
  if (!auth.currentUser || !schoolAccount || !schoolAccount.lastLoginAt) {
    return null
  }

  return (
    <div className="app-container">
      {/* モバイル時のオーバーレイ */}
      {isMobile && isMenuOpen && (
        <div className="sidebar-overlay" onClick={handleOverlayClick}></div>
      )}
      <aside className={`sidebar ${isMobile ? (isMenuOpen ? 'open' : 'closed') : (isMenuOpen ? 'open' : 'collapsed')}`}>
        <div className="sidebar-header">
          <img
            src={isMenuOpen ? logoL : logoM}
            alt="IROGAMI"
            className="sidebar-logo"
            onClick={() => navigate('/dashboards')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <Menu isCollapsed={!isMobile && !isMenuOpen} />
        <div className="sidebar-contact">
          <div className="sidebar-contact__divider" />
          <p className="sidebar-contact__title">ご利用校様専用窓口</p>
          <p className="sidebar-contact__phone">
            <i className="bi bi-telephone-fill" aria-hidden="true"></i>
            <span>0120-1653-16</span>
          </p>
          <p className="sidebar-contact__hours">平日 11:00～17:00</p>
          <div className="sidebar-contact__divider" />
        </div>
      </aside>
      <div className="main-content-container">
        <header className="top-navbar">
          <div className="nav-left">
            <button 
              className="menu-toggle" 
              onClick={toggleMenu} 
              aria-label="メニューを開く/閉じる"
            >
              <i className="bi bi-list"></i>
            </button>
          </div>
          <div className="nav-right">
            <Dropdown align="end">
              <DropdownToggle as="div" className="user-info-wrapper dropdown-toggle" style={{ cursor: 'pointer' }}>
                {!schoolAccountLoading && schoolAccount && (
                  <i className="bi bi-building school-icon"></i>
                )}
                <span className="user-info">
                  {schoolAccountLoading ? '読み込み中...' : schoolAccount?.schoolName ?? '学校'}
                </span>
              </DropdownToggle>
              <DropdownMenu align="end">
                <DropdownItem as={Link} to="/contract">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  登録情報
                </DropdownItem>
                <DropdownItem onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right me-2"></i>
                  ログアウト
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </header>
        <main className="page-content">
          <Suspense
            fallback={
              <div className="page-content-route-loading">
                <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export default MainLayout

